import { useTenantScope } from "../scope/TenantScopeContext";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { apiJson } from "../api/client";

import {
  getSelectedCompanyId,
} from "../utils/tenantContext";

import "./PolicyTruthPage.css";

function arrayOf(value, key) {
  if (Array.isArray(value)) return value;

  if (
    value &&
    key &&
    Array.isArray(value[key])
  ) {
    return value[key];
  }

  if (Array.isArray(value?.items)) {
    return value.items;
  }

  return [];
}

function shortUuid(value) {
  if (!value) return "—";
  return `${String(value).slice(0, 8)}…`;
}

function platformName(value) {
  const platform =
    String(value || "").toLowerCase();

  if (
    platform === "darwin" ||
    platform.startsWith("mac")
  ) {
    return "macOS";
  }

  if (platform.startsWith("windows")) {
    return "Windows";
  }

  return value || "Unknown";
}

function TruthState({
  label,
  state,
  detail,
}) {
  const normalized =
    String(state || "unknown")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-");

  return (
    <div className="ptr-stage">
      <span>{label}</span>

      <strong
        className={`ptr-state ptr-${normalized}`}
      >
        {state}
      </strong>

      <small>{detail || "—"}</small>
    </div>
  );
}

export default function PolicyTruthPage() {
  const globalScope = useTenantScope();
  const companyId =
    getSelectedCompanyId();

  const [data, setData] =
    useState({
      devices: [],
      assignments: [],
      acknowledgments: [],
      states: [],
      capabilities: [],
      reports: [],
    });

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [updated, setUpdated] =
    useState(null);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [healthFilter, setHealthFilter] =
    useState("ALL");

  const [platformFilter, setPlatformFilter] =
    useState("ALL");

  // R12I_POLICY_TRUTH_GLOBAL_SCOPE_SYNC
  useEffect(() => {
    const mappedPlatform =
      globalScope.platform === "windows"
        ? "WINDOWS"
        : globalScope.platform === "macos"
        ? "MACOS"
        : "ALL";

    setPlatformFilter(mappedPlatform);
  }, [globalScope.platform]);



  const [freshnessFilter, setFreshnessFilter] =
    useState("ALL");

  const [attentionOnly, setAttentionOnly] =
    useState(false);

  const load = useCallback(
    async () => {
      if (!companyId) {
        setError(
          "Select a company in Enrollment Management first."
        );

        setLoading(false);
        return;
      }

      setError("");

      const suffix =
        `?company_id=${encodeURIComponent(
          companyId
        )}`;

      const requests = [
        [
          "devices",
          `/device-status/devices${suffix}`,
        ],
        [
          "assignments",
          `/policy-assignment/assignments${suffix}`,
        ],
        [
          "acknowledgments",
          `/policy-assignment/acknowledgments${suffix}`,
        ],
        [
          "states",
          `/policy-enforcement/states${suffix}`,
        ],
        [
          "capabilities",
          `/policy-platform/capabilities${suffix}`,
        ],
        [
          "reports",
          `/policy-platform/reports${suffix}`,
        ],
      ];

      const result = {};

      await Promise.all(
        requests.map(
          async ([key, path]) => {
            try {
              result[key] =
                await apiJson(path);
            } catch (err) {
              console.error(
                `Policy Truth ${key}:`,
                err
              );

              result[key] = [];
            }
          }
        )
      );

      setData({
        devices:
          arrayOf(
            result.devices,
            "devices"
          ),

        assignments:
          arrayOf(
            result.assignments,
            "assignments"
          ),

        acknowledgments:
          arrayOf(
            result.acknowledgments,
            "acknowledgments"
          ),

        states:
          arrayOf(
            result.states,
            "states"
          ),

        capabilities:
          arrayOf(
            result.capabilities,
            "capabilities"
          ),

        reports:
          arrayOf(
            result.reports,
            "reports"
          ),
      });

      setUpdated(new Date());
      setLoading(false);
    },
    [companyId]
  );

  useEffect(() => {
    load();

    const timer =
      window.setInterval(
        load,
        30000
      );

    return () =>
      window.clearInterval(timer);
  }, [load]);

  const activeDevices =
    useMemo(
      () =>
        data.devices.filter(
          (device) =>
            String(
              device.lifecycle_state ||
                "active"
            ).toLowerCase() ===
            "active"
        ),
      [data.devices]
    );

  const rows =
    useMemo(
      () =>
        activeDevices.map(
          (device) => {
            const uuid =
              device.device_uuid;

            const assignment =
              data.assignments.find(
                (item) =>
                  item.device_uuid === uuid &&
                  item.status === "active"
              ) ||
              data.assignments.find(
                (item) =>
                  item.device_uuid === uuid
              );

            /*
             * Correlate every downstream stage to the
             * current canonical assignment/version.
             * Historical rows for the same device must
             * never satisfy current policy truth.
             */
            const ack =
              assignment
                ? data.acknowledgments.find(
                    (item) =>
                      item.device_uuid === uuid &&
                      item.assignment_uuid ===
                        assignment.assignment_uuid &&
                      item.version_uuid ===
                        assignment.version_uuid &&
                      (
                        !assignment.content_sha256 ||
                        !item.content_sha256 ||
                        item.content_sha256 ===
                          assignment.content_sha256
                      )
                  )
                : null;

            const enforcement =
              assignment
                ? data.states.find(
                    (item) =>
                      item.device_uuid === uuid &&
                      item.assignment_uuid ===
                        assignment.assignment_uuid &&
                      item.version_uuid ===
                        assignment.version_uuid &&
                      item.status !== "superseded"
                  ) ||
                  data.states.find(
                    (item) =>
                      item.device_uuid === uuid &&
                      item.assignment_uuid ===
                        assignment.assignment_uuid &&
                      item.version_uuid ===
                        assignment.version_uuid
                  )
                : null;

            const capability =
              data.capabilities.find(
                (item) =>
                  item.device_uuid === uuid
              );

            const report =
              enforcement
                ? data.reports.find(
                    (item) =>
                      item.device_uuid === uuid &&
                      item.enforcement_uuid ===
                        enforcement.enforcement_uuid &&
                      (
                        !assignment?.version_uuid ||
                        item.policy_version_uuid ===
                          assignment.version_uuid
                      )
                  )
                : null;

            const supported =
              capability
                ?.supported_control_codes ||
              [];

            const requested =
              report?.requested_control_codes ||
              [];

            const supportedSet =
              new Set(supported);

            const requiredCapabilitySatisfied =
              requested.length > 0
                ? requested.every(
                    (code) =>
                      supportedSet.has(code)
                  )
                : Boolean(capability);

            const assigned =
              Boolean(assignment);

            const acknowledged =
              Boolean(ack);

            /*
             * There is no independent durable
             * "delivery confirmed" table.
             * A receipt acknowledgment proves
             * delivery; otherwise delivery is
             * intentionally shown unconfirmed.
             */
            const delivered =
              acknowledged;

            let capabilityState =
              "MISSING";

            let capabilityDetail =
              "No capability report";

            if (capability) {
              if (
                supported.length === 0
              ) {
                capabilityState =
                  "BLOCKED";

                capabilityDetail =
                  "0 platform controls supported";
              } else if (
                requiredCapabilitySatisfied
              ) {
                capabilityState =
                  "SUPPORTED";

                capabilityDetail =
                  requested.length > 0
                    ? `${requested.length}/${requested.length} required controls supported`
                    : `${supported.length} controls reported`;
              } else {
                const missing =
                  requested.filter(
                    (code) =>
                      !supportedSet.has(code)
                  );

                capabilityState =
                  "BLOCKED";

                capabilityDetail =
                  `Missing: ${missing.join(", ")}`;
              }
            }

            let appliedState =
              "NOT APPLIED";

            let appliedDetail =
              "No enforcement state";

            if (enforcement) {
              appliedState =
                String(
                  enforcement.status ||
                    "unknown"
                ).toUpperCase();

              appliedDetail =
                `Mode ${
                  enforcement.desired_mode ||
                  "unknown"
                }`;
            }

            if (
              enforcement?.status ===
              "applied"
            ) {
              appliedState =
                "APPLIED";
            }

            let verifiedState =
              "NO REPORT";

            let verifiedDetail =
              "No platform application report";

            if (report) {
              const verification =
                report.verification_results;

              const hasVerification =
                verification &&
                (
                  Array.isArray(
                    verification
                  )
                    ? verification.length > 0
                    : Object.keys(
                        verification
                      ).length > 0
                );

              const allRequestedVerified =
                requested.length > 0 &&
                !Array.isArray(verification) &&
                requested.every(
                  (code) =>
                    verification?.[code] === true
                );

              if (
                hasVerification &&
                allRequestedVerified &&
                [
                  "applied",
                  "verified",
                  "success",
                ].includes(
                  String(
                    report.report_status ||
                      ""
                  ).toLowerCase()
                )
              ) {
                verifiedState =
                  "VERIFIED";
              } else {
                verifiedState =
                  String(
                    report.report_status ||
                      "UNVERIFIED"
                  ).toUpperCase();
              }

              verifiedDetail =
                report.error_message ||
                `${
                  (
                    report.applied_control_codes ||
                    []
                  ).length
                } controls applied`;
            }

            const appliedControls =
              report?.applied_control_codes ||
              [];

            const verificationResults =
              report?.verification_results &&
              !Array.isArray(
                report.verification_results
              )
                ? report.verification_results
                : {};

            const verifiedControls =
              requested.filter(
                (code) =>
                  verificationResults?.[code] === true
              );

            const enforcementStatus =
              String(
                enforcement?.status || ""
              ).toLowerCase();

            const killSwitchEnabled =
              capability
                ?.enforcement_kill_switch_enabled;

            const nowMs =
              Date.now();

            const lastSeenMs =
              device?.last_seen
                ? new Date(
                    device.last_seen
                  ).getTime()
                : 0;

            const lastSeenAgeMs =
              lastSeenMs > 0
                ? Math.max(
                    0,
                    nowMs - lastSeenMs
                  )
                : null;

            const lastSeenAgeMinutes =
              lastSeenAgeMs == null
                ? null
                : Math.floor(
                    lastSeenAgeMs / 60000
                  );

            let freshnessState =
              "OFFLINE";

            if (
              lastSeenAgeMinutes != null &&
              lastSeenAgeMinutes <= 10
            ) {
              freshnessState =
                "CURRENT";
            } else if (
              lastSeenAgeMinutes != null &&
              lastSeenAgeMinutes <= 60
            ) {
              freshnessState =
                "STALE";
            }

            let overallHealth =
              "UNVERIFIED";

            if (!assigned) {
              overallHealth =
                "NO POLICY";
            } else if (
              capabilityState === "BLOCKED" ||
              killSwitchEnabled === false
            ) {
              overallHealth =
                "BLOCKED";
            } else if (
              enforcementStatus === "applied" &&
              verifiedState === "VERIFIED"
            ) {
              overallHealth =
                freshnessState === "CURRENT"
                  ? "HEALTHY"
                  : "DEGRADED";
            } else if (
              [
                "failed",
                "rolled_back",
                "unsupported",
                "error",
              ].includes(enforcementStatus) ||
              Boolean(
                enforcement?.last_error_code
              ) ||
              Number(
                enforcement?.failure_count || 0
              ) > 0
            ) {
              overallHealth =
                "DEGRADED";
            } else {
              overallHealth =
                "UNVERIFIED";
            }

            return {
              device,
              assignment,
              ack,
              enforcement,
              capability,
              report,

              operational: {
                overallHealth,
                freshnessState,
                lastSeenAgeMinutes,
                requested,
                appliedControls,
                verifiedControls,
              },

              stages: {
                assigned: {
                  state:
                    assigned
                      ? "ASSIGNED"
                      : "NONE",

                  detail:
                    assignment
                      ? `${assignment.policy_name || "Policy"} ${
                          assignment.version_number
                            ? `v${assignment.version_number}`
                            : ""
                        }`
                      : "No canonical assignment",
                },

                delivered: {
                  state:
                    delivered
                      ? "CONFIRMED"
                      : assigned
                      ? "UNCONFIRMED"
                      : "N/A",

                  detail:
                    acknowledged
                      ? "Receipt acknowledgment proves delivery"
                      : assigned
                      ? "No receipt acknowledgment"
                      : "No assignment",
                },

                acknowledged: {
                  state:
                    acknowledged
                      ? "RECEIVED"
                      : assigned
                      ? "WAITING"
                      : "N/A",

                  detail:
                    ack
                      ? shortUuid(
                          ack.acknowledgment_uuid
                        )
                      : "No canonical acknowledgment",
                },

                capability: {
                  state:
                    capabilityState,

                  detail:
                    capabilityDetail,
                },

                applied: {
                  state:
                    appliedState,

                  detail:
                    appliedDetail,
                },

                verified: {
                  state:
                    verifiedState,

                  detail:
                    verifiedDetail,
                },
              },
            };
          }
        ),
      [
        activeDevices,
        data.assignments,
        data.acknowledgments,
        data.states,
        data.capabilities,
        data.reports,
      ]
    );

  const filteredRows =
    useMemo(() => {
      const search =
        searchTerm.trim().toLowerCase();

      const filtered =
        rows.filter((row) => {
        const health =
          String(
            row.operational?.overallHealth ||
              "UNVERIFIED"
          ).toUpperCase();

        // R12I_POLICY_TRUTH_DEVICE_SCOPE
        if (
          globalScope.deviceUuid &&
          String(row.device?.device_uuid || "") !==
            String(globalScope.deviceUuid)
        ) {
          return false;
        }

        const platform =
          platformName(
            row.device?.platform
          ).toUpperCase();

        const searchable =
          [
            row.device?.device_name,
            row.device?.hostname,
            row.device?.device_uuid,
            row.assignment?.policy_name,
            row.assignment?.assignment_uuid,
            row.enforcement?.enforcement_uuid,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

        if (
          search &&
          !searchable.includes(search)
        ) {
          return false;
        }

        if (
          healthFilter !== "ALL" &&
          health !== healthFilter
        ) {
          return false;
        }

        if (
          platformFilter !== "ALL" &&
          platform !== platformFilter
        ) {
          return false;
        }

        const freshness =
          String(
            row.operational?.freshnessState ||
              "OFFLINE"
          ).toUpperCase();

        if (
          freshnessFilter !== "ALL" &&
          freshness !== freshnessFilter
        ) {
          return false;
        }

        if (
          attentionOnly &&
          health === "HEALTHY"
        ) {
          return false;
        }

        return true;
      });

      const healthPriority = {
        BLOCKED: 0,
        DEGRADED: 1,
        UNVERIFIED: 2,
        "NO POLICY": 3,
        HEALTHY: 4,
      };

      return filtered.sort((a, b) => {
        const aHealth =
          String(
            a.operational?.overallHealth ||
              "UNVERIFIED"
          ).toUpperCase();

        const bHealth =
          String(
            b.operational?.overallHealth ||
              "UNVERIFIED"
          ).toUpperCase();

        const aHealthRank =
          healthPriority[aHealth] ?? 99;

        const bHealthRank =
          healthPriority[bHealth] ?? 99;

        if (aHealthRank !== bHealthRank) {
          return aHealthRank - bHealthRank;
        }

        const freshnessPriority = {
          OFFLINE: 0,
          STALE: 1,
          CURRENT: 2,
        };

        const aFreshness =
          String(
            a.operational?.freshnessState ||
              "OFFLINE"
          ).toUpperCase();

        const bFreshness =
          String(
            b.operational?.freshnessState ||
              "OFFLINE"
          ).toUpperCase();

        const aFreshnessRank =
          freshnessPriority[aFreshness] ?? 99;

        const bFreshnessRank =
          freshnessPriority[bFreshness] ?? 99;

        if (
          aFreshnessRank !== bFreshnessRank
        ) {
          return (
            aFreshnessRank -
            bFreshnessRank
          );
        }

        const aFailures =
          Number(
            a.enforcement?.failure_count || 0
          );

        const bFailures =
          Number(
            b.enforcement?.failure_count || 0
          );

        if (aFailures !== bFailures) {
          return bFailures - aFailures;
        }

        const aHasError =
          Boolean(
            a.enforcement?.last_error_code ||
            a.enforcement?.last_error_message
          );

        const bHasError =
          Boolean(
            b.enforcement?.last_error_code ||
            b.enforcement?.last_error_message
          );

        if (aHasError !== bHasError) {
          return aHasError ? -1 : 1;
        }

        const aSeen =
          a.device?.last_seen
            ? new Date(
                a.device.last_seen
              ).getTime()
            : 0;

        const bSeen =
          b.device?.last_seen
            ? new Date(
                b.device.last_seen
              ).getTime()
            : 0;

        if (aSeen !== bSeen) {
          return aSeen - bSeen;
        }

        const aName =
          String(
            a.device?.device_name ||
            a.device?.hostname ||
            a.device?.device_uuid ||
            ""
          ).toLowerCase();

        const bName =
          String(
            b.device?.device_name ||
            b.device?.hostname ||
            b.device?.device_uuid ||
            ""
          ).toLowerCase();

        return aName.localeCompare(bName);
      });
    }, [
      rows,
      searchTerm,
      healthFilter,
      platformFilter,
      freshnessFilter,
      attentionOnly,
      globalScope.deviceUuid,
    ]);

  const healthCounts =
    useMemo(() => {
      const counts = {
        HEALTHY: 0,
        DEGRADED: 0,
        BLOCKED: 0,
        UNVERIFIED: 0,
        "NO POLICY": 0,
      };

      rows.forEach((row) => {
        const state =
          String(
            row.operational?.overallHealth ||
              "UNVERIFIED"
          ).toUpperCase();

        if (
          Object.prototype.hasOwnProperty.call(
            counts,
            state
          )
        ) {
          counts[state] += 1;
        }
      });

      return counts;
    }, [rows]);

  const needsAttention =
    healthCounts.DEGRADED +
    healthCounts.BLOCKED +
    healthCounts.UNVERIFIED +
    healthCounts["NO POLICY"];

  if (loading) {
    return (
      <div className="ptr-loading">
        Loading policy truth…
      </div>
    );
  }

  return (
    <div className="ptr-page">
      <header className="ptr-header">
        <div>
          <span>
            CANONICAL POLICY STATE
          </span>

          <h1>
            Policy Enforcement Truth
          </h1>

          <p>
            Assignment is not enforcement.
            Every stage is shown independently.
          </p>
        </div>

        <div className="ptr-header-meta">
          <strong>
            Company {companyId}
          </strong>

          <small>
            {updated
              ? updated.toLocaleTimeString()
              : "—"}
          </small>
        </div>
      </header>

      {error && (
        <div className="ptr-error">
          {error}
        </div>
      )}

      <section className="ptr-summary">
        <div>
          <span>
            Active endpoints
          </span>
          <strong>
            {activeDevices.length}
          </strong>
        </div>

        <div>
          <span>
            Assignments
          </span>
          <strong>
            {data.assignments.filter(
              (item) =>
                item.status === "active"
            ).length}
          </strong>
        </div>

        <div>
          <span>
            Acknowledgments
          </span>
          <strong>
            {data.acknowledgments.length}
          </strong>
        </div>

        <div>
          <span>
            Enforcement states
          </span>
          <strong>
            {data.states.length}
          </strong>
        </div>

        <div>
          <span>
            Platform reports
          </span>
          <strong>
            {data.reports.length}
          </strong>
        </div>
      </section>

      <section
        className="ptr-analyst-filters"
        style={{
          margin: "18px 0",
          padding: "16px",
          borderRadius: "14px",
          background: "rgba(8, 15, 30, 0.72)",
          border:
            "1px solid rgba(148, 163, 184, 0.18)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "12px",
          }}
        >
          <div>
            <small
              style={{
                display: "block",
                opacity: 0.65,
                marginBottom: "5px",
              }}
            >
              Search endpoint
            </small>

            <input
              type="search"
              value={searchTerm}
              onChange={(event) =>
                setSearchTerm(
                  event.target.value
                )
              }
              placeholder="Name, UUID, policy..."
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "9px",
              }}
            />
          </div>

          <div>
            <small
              style={{
                display: "block",
                opacity: 0.65,
                marginBottom: "5px",
              }}
            >
              Health
            </small>

            <select
              value={healthFilter}
              onChange={(event) =>
                setHealthFilter(
                  event.target.value
                )
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "9px",
              }}
            >
              <option value="ALL">
                All health states
              </option>
              <option value="HEALTHY">
                Healthy
              </option>
              <option value="DEGRADED">
                Degraded
              </option>
              <option value="BLOCKED">
                Blocked
              </option>
              <option value="UNVERIFIED">
                Unverified
              </option>
              <option value="NO POLICY">
                No Policy
              </option>
            </select>
          </div>

          <div>
            <small
              style={{
                display: "block",
                opacity: 0.65,
                marginBottom: "5px",
              }}
            >
              Platform
            </small>

            <select
              value={platformFilter}
              onChange={(event) =>
                setPlatformFilter(
                  event.target.value
                )
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "9px",
              }}
            >
              <option value="ALL">
                All platforms
              </option>
              <option value="WINDOWS">
                Windows
              </option>
              <option value="MACOS">
                macOS
              </option>
            </select>
          </div>

          <div>
            <small
              style={{
                display: "block",
                opacity: 0.65,
                marginBottom: "5px",
              }}
            >
              Freshness
            </small>

            <select
              value={freshnessFilter}
              onChange={(event) =>
                setFreshnessFilter(
                  event.target.value
                )
              }
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "9px",
              }}
            >
              <option value="ALL">
                All freshness states
              </option>
              <option value="CURRENT">
                Current
              </option>
              <option value="STALE">
                Stale
              </option>
              <option value="OFFLINE">
                Offline
              </option>
            </select>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "end",
            }}
          >
            <label
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                padding: "10px 0",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={attentionOnly}
                onChange={(event) =>
                  setAttentionOnly(
                    event.target.checked
                  )
                }
              />

              Attention only
            </label>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(130px, 1fr))",
            gap: "10px",
            marginTop: "14px",
          }}
        >
          {[
            [
              "Healthy",
              healthCounts.HEALTHY,
            ],
            [
              "Needs attention",
              needsAttention,
            ],
            [
              "Degraded",
              healthCounts.DEGRADED,
            ],
            [
              "Blocked",
              healthCounts.BLOCKED,
            ],
            [
              "Unverified",
              healthCounts.UNVERIFIED,
            ],
            [
              "No Policy",
              healthCounts["NO POLICY"],
            ],
            [
              "Showing",
              `${filteredRows.length}/${rows.length}`,
            ],
          ].map(([label, value]) => (
            <div
              key={label}
              style={{
                padding: "10px 12px",
                borderRadius: "9px",
                background:
                  "rgba(255,255,255,0.035)",
              }}
            >
              <small
                style={{
                  display: "block",
                  opacity: 0.62,
                }}
              >
                {label}
              </small>

              <strong>{value}</strong>
            </div>
          ))}
        </div>
      </section>

      <div className="ptr-device-list">
        {filteredRows.map((row) => (
          <section
            className="ptr-device-card"
            key={row.device.device_uuid}
          >
            <div className="ptr-device-head">
              <div>
                <span>
                  {platformName(
                    row.device.platform
                  )}
                </span>

                <strong>
                  {row.device.device_name ||
                    row.device.hostname ||
                    shortUuid(
                      row.device.device_uuid
                    )}
                </strong>

                <small>
                  {row.device.device_uuid}
                </small>
              </div>

              <div>
                <b>
                  {row.device.effective_status ||
                    "unknown"}
                </b>

                <small>
                  Legacy policy ID:{" "}
                  {row.device.policy_id ??
                    "none"}
                </small>
              </div>
            </div>

            <div
              className="ptr-operational"
              style={{
                marginTop: "18px",
                marginBottom: "18px",
                padding: "18px",
                borderRadius: "14px",
                background:
                  "rgba(8, 15, 30, 0.72)",
                border:
                  "1px solid rgba(148, 163, 184, 0.18)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  gap: "16px",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: "16px",
                }}
              >
                <div>
                  <small
                    style={{
                      display: "block",
                      opacity: 0.7,
                      marginBottom: "4px",
                    }}
                  >
                    ENDPOINT POLICY HEALTH
                  </small>

                  <strong
                    style={{
                      fontSize: "1.35rem",
                    }}
                  >
                    {
                      row.operational
                        .overallHealth
                    }
                  </strong>
                </div>

                <div
                  style={{
                    textAlign: "right",
                  }}
                >
                  <strong>
                    {
                      row.assignment
                        ?.policy_name ||
                      "No active policy"
                    }
                    {
                      row.assignment
                        ?.version_number
                        ? ` v${row.assignment.version_number}`
                        : ""
                    }
                  </strong>

                  <small
                    style={{
                      display: "block",
                      opacity: 0.7,
                      marginTop: "4px",
                    }}
                  >
                    {
                      row.enforcement
                        ?.generation != null
                        ? `Generation ${row.enforcement.generation}`
                        : "No enforcement generation"
                    }
                  </small>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "10px",
                  marginBottom: "16px",
                }}
              >
                {[
                  [
                    "Freshness",
                    row.operational
                      .freshnessState,
                  ],
                  [
                    "Last seen age",
                    row.operational
                      .lastSeenAgeMinutes == null
                      ? "Unknown"
                      : `${row.operational.lastSeenAgeMinutes} min`,
                  ],
                  [
                    "Mode",
                    row.enforcement
                      ?.desired_mode ||
                      "—",
                  ],
                  [
                    "Attempts",
                    row.enforcement
                      ?.attempt_count ??
                      "—",
                  ],
                  [
                    "Failures",
                    row.enforcement
                      ?.failure_count ??
                      "—",
                  ],
                  [
                    "Requested",
                    row.operational
                      .requested.length,
                  ],
                  [
                    "Applied",
                    row.operational
                      .appliedControls.length,
                  ],
                  [
                    "Verified",
                    row.operational
                      .verifiedControls.length,
                  ],
                  [
                    "Supported",
                    (
                      row.capability
                        ?.supported_control_codes ||
                      []
                    ).length,
                  ],
                ].map(
                  ([label, value]) => (
                    <div
                      key={label}
                      style={{
                        padding:
                          "10px 12px",
                        borderRadius:
                          "10px",
                        background:
                          "rgba(255,255,255,0.035)",
                      }}
                    >
                      <small
                        style={{
                          display:
                            "block",
                          opacity: 0.62,
                        }}
                      >
                        {label}
                      </small>

                      <strong>
                        {value}
                      </strong>
                    </div>
                  )
                )}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(260px, 1fr))",
                  gap: "12px",
                }}
              >
                <div>
                  <small
                    style={{
                      opacity: 0.65,
                    }}
                  >
                    Assignment
                  </small>

                  <div
                    style={{
                      wordBreak:
                        "break-all",
                    }}
                  >
                    {
                      row.assignment
                        ?.assignment_uuid ||
                      "—"
                    }
                  </div>
                </div>

                <div>
                  <small
                    style={{
                      opacity: 0.65,
                    }}
                  >
                    Enforcement
                  </small>

                  <div
                    style={{
                      wordBreak:
                        "break-all",
                    }}
                  >
                    {
                      row.enforcement
                        ?.enforcement_uuid ||
                      "—"
                    }
                  </div>
                </div>

                <div>
                  <small
                    style={{
                      opacity: 0.65,
                    }}
                  >
                    Last device check-in
                  </small>

                  <div>
                    {
                      row.device.last_seen
                        ? new Date(
                            row.device.last_seen
                          ).toLocaleString()
                        : "—"
                    }
                  </div>
                </div>

                <div>
                  <small
                    style={{
                      opacity: 0.65,
                    }}
                  >
                    Capability reported
                  </small>

                  <div>
                    {
                      row.capability
                        ?.reported_at
                        ? new Date(
                            row.capability.reported_at
                          ).toLocaleString()
                        : "—"
                    }
                  </div>
                </div>

                <div>
                  <small
                    style={{
                      opacity: 0.65,
                    }}
                  >
                    Enforcement updated
                  </small>

                  <div>
                    {
                      row.enforcement
                        ?.updated_at
                        ? new Date(
                            row.enforcement.updated_at
                          ).toLocaleString()
                        : "—"
                    }
                  </div>
                </div>

                <div>
                  <small
                    style={{
                      opacity: 0.65,
                    }}
                  >
                    Last error
                  </small>

                  <div>
                    {
                      row.enforcement
                        ?.last_error_code ||
                      row.enforcement
                        ?.last_error_message ||
                      "None"
                    }
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: "16px",
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(230px, 1fr))",
                  gap: "12px",
                }}
              >
                <div>
                  <small
                    style={{
                      opacity: 0.65,
                    }}
                  >
                    Requested controls
                  </small>

                  <div>
                    {
                      row.operational
                        .requested.length
                        ? row.operational
                            .requested
                            .join(", ")
                        : "None"
                    }
                  </div>
                </div>

                <div>
                  <small
                    style={{
                      opacity: 0.65,
                    }}
                  >
                    Applied controls
                  </small>

                  <div>
                    {
                      row.operational
                        .appliedControls
                        .length
                        ? row.operational
                            .appliedControls
                            .join(", ")
                        : "None"
                    }
                  </div>
                </div>

                <div>
                  <small
                    style={{
                      opacity: 0.65,
                    }}
                  >
                    Verified controls
                  </small>

                  <div>
                    {
                      row.operational
                        .verifiedControls
                        .length
                        ? row.operational
                            .verifiedControls
                            .join(", ")
                        : "None"
                    }
                  </div>
                </div>
              </div>
            </div>

            <details
              className="ptr-evidence"
              style={{
                margin: "0 0 18px 0",
                borderRadius: "12px",
                border:
                  "1px solid rgba(148, 163, 184, 0.18)",
                background:
                  "rgba(8, 15, 30, 0.55)",
                overflow: "hidden",
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  padding: "14px 16px",
                  fontWeight: 700,
                  userSelect: "none",
                }}
              >
                Evidence Details
              </summary>

              <div
                style={{
                  padding: "0 16px 16px",
                  display: "grid",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {[
                    [
                      "Assignment UUID",
                      row.assignment
                        ?.assignment_uuid,
                    ],
                    [
                      "Policy UUID",
                      row.assignment
                        ?.policy_uuid,
                    ],
                    [
                      "Version UUID",
                      row.assignment
                        ?.version_uuid,
                    ],
                    [
                      "Content SHA256",
                      row.assignment
                        ?.content_sha256,
                    ],
                    [
                      "Acknowledgment UUID",
                      row.ack
                        ?.acknowledgment_uuid,
                    ],
                    [
                      "Acknowledgment state",
                      row.ack
                        ?.acknowledgment_state,
                    ],
                    [
                      "Enforcement UUID",
                      row.enforcement
                        ?.enforcement_uuid,
                    ],
                    [
                      "Generation",
                      row.enforcement
                        ?.generation,
                    ],
                    [
                      "Capability UUID",
                      row.capability
                        ?.capability_uuid,
                    ],
                    [
                      "Agent version",
                      row.capability
                        ?.agent_version,
                    ],
                    [
                      "Platform release",
                      row.capability
                        ?.platform_release,
                    ],
                    [
                      "Kill switch",
                      row.capability
                        ?.enforcement_kill_switch_enabled ===
                      true
                        ? "ENABLED"
                        : row.capability
                            ?.enforcement_kill_switch_enabled ===
                          false
                        ? "DISABLED"
                        : "UNKNOWN",
                    ],
                    [
                      "Report UUID",
                      row.report
                        ?.report_uuid,
                    ],
                    [
                      "Report status",
                      row.report
                        ?.report_status,
                    ],
                  ].map(
                    ([label, value]) => (
                      <div
                        key={label}
                        style={{
                          padding:
                            "10px 12px",
                          borderRadius:
                            "9px",
                          background:
                            "rgba(255,255,255,0.03)",
                        }}
                      >
                        <small
                          style={{
                            display:
                              "block",
                            opacity:
                              0.62,
                            marginBottom:
                              "4px",
                          }}
                        >
                          {label}
                        </small>

                        <div
                          style={{
                            wordBreak:
                              "break-all",
                          }}
                        >
                          {value ??
                            "—"}
                        </div>
                      </div>
                    )
                  )}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "12px",
                  }}
                >
                  <div>
                    <small
                      style={{
                        opacity: 0.62,
                      }}
                    >
                      Requested controls
                    </small>

                    <pre
                      style={{
                        whiteSpace:
                          "pre-wrap",
                        wordBreak:
                          "break-word",
                      }}
                    >
                      {JSON.stringify(
                        row.report
                          ?.requested_control_codes ||
                          [],
                        null,
                        2
                      )}
                    </pre>
                  </div>

                  <div>
                    <small
                      style={{
                        opacity: 0.62,
                      }}
                    >
                      Applied controls
                    </small>

                    <pre
                      style={{
                        whiteSpace:
                          "pre-wrap",
                        wordBreak:
                          "break-word",
                      }}
                    >
                      {JSON.stringify(
                        row.report
                          ?.applied_control_codes ||
                          [],
                        null,
                        2
                      )}
                    </pre>
                  </div>

                  <div>
                    <small
                      style={{
                        opacity: 0.62,
                      }}
                    >
                      Verification results
                    </small>

                    <pre
                      style={{
                        whiteSpace:
                          "pre-wrap",
                        wordBreak:
                          "break-word",
                      }}
                    >
                      {JSON.stringify(
                        row.report
                          ?.verification_results ||
                          {},
                        null,
                        2
                      )}
                    </pre>
                  </div>

                  <div>
                    <small
                      style={{
                        opacity: 0.62,
                      }}
                    >
                      Supported controls
                    </small>

                    <pre
                      style={{
                        whiteSpace:
                          "pre-wrap",
                        wordBreak:
                          "break-word",
                      }}
                    >
                      {JSON.stringify(
                        row.capability
                          ?.supported_control_codes ||
                          [],
                        null,
                        2
                      )}
                    </pre>
                  </div>

                  <div>
                    <small
                      style={{
                        opacity: 0.62,
                      }}
                    >
                      Unsupported reasons
                    </small>

                    <pre
                      style={{
                        whiteSpace:
                          "pre-wrap",
                        wordBreak:
                          "break-word",
                      }}
                    >
                      {JSON.stringify(
                        row.capability
                          ?.unsupported_control_reasons ||
                          {},
                        null,
                        2
                      )}
                    </pre>
                  </div>

                  <div>
                    <small
                      style={{
                        opacity: 0.62,
                      }}
                    >
                      Adapter details
                    </small>

                    <pre
                      style={{
                        whiteSpace:
                          "pre-wrap",
                        wordBreak:
                          "break-word",
                      }}
                    >
                      {JSON.stringify(
                        row.capability
                          ?.adapter_details ||
                          {},
                        null,
                        2
                      )}
                    </pre>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fit, minmax(250px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {[
                    [
                      "Assignment created",
                      row.assignment
                        ?.created_at,
                    ],
                    [
                      "Acknowledgment received",
                      row.ack
                        ?.received_at,
                    ],
                    [
                      "Capability reported",
                      row.capability
                        ?.reported_at,
                    ],
                    [
                      "Enforcement created",
                      row.enforcement
                        ?.created_at,
                    ],
                    [
                      "Enforcement updated",
                      row.enforcement
                        ?.updated_at,
                    ],
                    [
                      "Platform report created",
                      row.report
                        ?.created_at,
                    ],
                  ].map(
                    ([label, value]) => (
                      <div key={label}>
                        <small
                          style={{
                            display:
                              "block",
                            opacity:
                              0.62,
                          }}
                        >
                          {label}
                        </small>

                        <div>
                          {value
                            ? new Date(
                                value
                              ).toLocaleString()
                            : "—"}
                        </div>
                      </div>
                    )
                  )}
                </div>

                {(row.enforcement
                  ?.last_error_code ||
                  row.enforcement
                    ?.last_error_message ||
                  row.report
                    ?.error_code ||
                  row.report
                    ?.error_message) && (
                  <div
                    style={{
                      padding: "12px",
                      borderRadius:
                        "9px",
                      background:
                        "rgba(255,255,255,0.035)",
                    }}
                  >
                    <strong>
                      Error evidence
                    </strong>

                    <pre
                      style={{
                        whiteSpace:
                          "pre-wrap",
                        wordBreak:
                          "break-word",
                      }}
                    >
                      {JSON.stringify(
                        {
                          enforcement_error_code:
                            row.enforcement
                              ?.last_error_code ||
                            null,

                          enforcement_error_message:
                            row.enforcement
                              ?.last_error_message ||
                            null,

                          report_error_code:
                            row.report
                              ?.error_code ||
                            null,

                          report_error_message:
                            row.report
                              ?.error_message ||
                            null,
                        },
                        null,
                        2
                      )}
                    </pre>
                  </div>
                )}
              </div>
            </details>

            <div className="ptr-pipeline">
              <TruthState
                label="Assigned"
                {...row.stages.assigned}
              />

              <TruthState
                label="Delivered"
                {...row.stages.delivered}
              />

              <TruthState
                label="Acknowledged"
                {...row.stages.acknowledged}
              />

              <TruthState
                label="Capability"
                {...row.stages.capability}
              />

              <TruthState
                label="Applied"
                {...row.stages.applied}
              />

              <TruthState
                label="Verified"
                {...row.stages.verified}
              />
            </div>

            {row.capability &&
              (
                row.capability
                  .supported_control_codes ||
                []
              ).length === 0 && (
                <div className="ptr-blocker">
                  <strong>
                    Enforcement blocker
                  </strong>

                  <span>
                    This endpoint reported
                    zero supported platform
                    controls.
                  </span>

                  {row.capability
                    .unsupported_control_reasons && (
                    <pre>
                      {JSON.stringify(
                        row.capability
                          .unsupported_control_reasons,
                        null,
                        2
                      )}
                    </pre>
                  )}
                </div>
              )}
          </section>
        ))}

        {!rows.length && (
          <div className="ptr-empty">
            No active devices for the
            selected company.
          </div>
        )}
      </div>
    </div>
  );
}
